import type { FieldChange, PrimaryEntityType } from '../types';

export interface ReciprocalDeltaPayload {
  entityType: PrimaryEntityType;
  entityId: string;
  entityName?: string; // Optional, might need fetching if not known
  changes: FieldChange[];
}

/**
 * Examines a list of deltas for a specific entity and generates reciprocal deltas
 * for related entities to maintain bidirectional consistency in time-travel state.
 */
export function generateReciprocalDeltas(
  sourceEntityType: PrimaryEntityType,
  sourceEntityId: string,
  sourceChanges: FieldChange[]
): ReciprocalDeltaPayload[] {
  const reciprocalPayloads: Map<string, ReciprocalDeltaPayload> = new Map();

  const addReciprocal = (type: PrimaryEntityType, id: string, change: FieldChange) => {
    const key = `${type}_${id}`;
    if (!reciprocalPayloads.has(key)) {
      reciprocalPayloads.set(key, { entityType: type, entityId: id, changes: [] });
    }
    reciprocalPayloads.get(key)!.changes.push(change);
  };

  for (const change of sourceChanges) {
    if (sourceEntityType === 'organizations') {
      if (change.field === 'organizationMembers') {
        const member = (change.newValue ?? change.value) as { characterId?: string } | undefined;
        const charId = change.operation === 'remove' ? change.identifier : member?.characterId;
        if (charId) {
          addReciprocal('characters', charId, {
            field: 'organizationIds',
            operation: change.operation,
            ...(change.operation === 'add' ? { newValue: sourceEntityId, value: sourceEntityId } : {}),
            ...(change.operation === 'remove' ? { identifier: sourceEntityId } : {}),
          });
        }
      } else if (change.field === 'childOrganizationIds') {
        const childOrgId = change.operation === 'remove' ? change.identifier : (change.value as string);
        if (childOrgId) {
          addReciprocal('organizations', childOrgId, {
            field: 'parentOrganizationIds',
            operation: change.operation,
            ...(change.operation === 'add' ? { value: sourceEntityId } : {}),
            ...(change.operation === 'remove' ? { identifier: sourceEntityId } : {}),
          });
        }
      } else if (change.field === 'parentOrganizationIds') {
        const parentOrgId = change.operation === 'remove' ? change.identifier : (change.value as string);
        if (parentOrgId) {
          addReciprocal('organizations', parentOrgId, {
            field: 'childOrganizationIds',
            operation: change.operation,
            ...(change.operation === 'add' ? { value: sourceEntityId } : {}),
            ...(change.operation === 'remove' ? { identifier: sourceEntityId } : {}),
          });
        }
      }
    } else if (sourceEntityType === 'characters') {
      if (change.field === 'organizationIds') {
        const rawVal = change.newValue ?? change.value;
        const orgId = change.operation === 'remove' ? change.identifier : (typeof rawVal === 'string' ? rawVal : (rawVal as { id?: string })?.id);
        if (orgId) {
          addReciprocal('organizations', orgId, {
            field: 'organizationMembers',
            operation: change.operation,
            ...(change.operation === 'add' ? { newValue: { characterId: sourceEntityId, title: 'Member' }, value: { characterId: sourceEntityId, title: 'Member' } } : {}),
            ...(change.operation === 'remove' ? { identifier: sourceEntityId } : {}),
          });
        }
      }
    }
    // Add location hierarchies or other bidirectional fields as needed
  }

  return Array.from(reciprocalPayloads.values());
}
